import React from 'react';

// Helper to safely strip HTML tags when we want pure text (e.g. headings)
const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').trim();
};

const BlogSection = ({ section }) => {
  const renderSection = () => {
    switch (section.type) {
      case 'text':
        return (
          <div 
            className="text-default text-lg leading-relaxed mb-6 prose prose-lg max-w-none dark:prose-invert blog-content"
            dangerouslySetInnerHTML={{ __html: section.content }}
          />
        );

      case 'heading':
        // Slate produces clean HTML with h1, h2, h3 tags - render directly
        return (
          <div 
            className="mb-6 mt-8 prose prose-lg max-w-none dark:prose-invert blog-content"
            dangerouslySetInnerHTML={{ __html: section.content }}
          />
        );

      case 'image':
        const imageContent = typeof section.content === 'string' 
          ? { url: section.content, alt: '', caption: '' }
          : section.content;
        return (
          <figure className="my-8">
            <img
              src={imageContent.url}
              alt={imageContent.alt || ''}
              className="w-full rounded-lg"
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/800x400?text=Image';
              }}
            />
            {imageContent.caption && (
              <figcaption className="text-sm text-gray-600 dark:text-gray-400 mt-2 text-center italic">
                {imageContent.caption}
              </figcaption>
            )}
          </figure>
        );

      case 'video':
        const videoContent = typeof section.content === 'string'
          ? { url: section.content, platform: 'youtube' }
          : section.content;
        
        const getVideoEmbed = () => {
          if (!videoContent.url) return null;
          
          if (videoContent.platform === 'youtube' || videoContent.url.includes('youtube.com') || videoContent.url.includes('youtu.be')) {
            const videoId = videoContent.url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1];
            if (videoId) {
              return (
                <div className="relative w-full pb-[56.25%] h-0 overflow-hidden rounded-lg my-8">
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src={`https://www.youtube.com/embed/${videoId}`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              );
            }
          } else if (videoContent.platform === 'vimeo' || videoContent.url.includes('vimeo.com')) {
            const videoId = videoContent.url.match(/vimeo\.com\/(\d+)/)?.[1];
            if (videoId) {
              return (
                <div className="relative w-full pb-[56.25%] h-0 overflow-hidden rounded-lg my-8">
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src={`https://player.vimeo.com/video/${videoId}`}
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              );
            }
          }
          
          return (
            <div className="my-8">
              <video controls className="w-full rounded-lg">
                <source src={videoContent.url} />
                Your browser does not support the video tag.
              </video>
            </div>
          );
        };

        return getVideoEmbed();

      case 'quote':
        return (
          <blockquote 
            className="border-l-4 border-accent pl-6 py-4 my-8 italic text-lg text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-r-lg prose prose-lg max-w-none dark:prose-invert"
            dangerouslySetInnerHTML={{ __html: section.content }}
          />
        );

      case 'code':
        const codeContent = typeof section.content === 'string'
          ? { code: section.content, language: 'javascript' }
          : section.content;
        return (
          <pre className="bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-4 rounded-lg overflow-x-auto my-8 border border-gray-300 dark:border-gray-700">
            <code className="text-sm font-mono text-gray-900 dark:text-gray-100">{codeContent.code}</code>
          </pre>
        );

      case 'table':
        // Slate produces clean HTML tables - render directly
        if (typeof section.content === 'string' && section.content.includes('<table')) {
          return (
            <div className="my-8 overflow-x-auto">
              <div 
                className="prose prose-lg max-w-none dark:prose-invert blog-content"
                dangerouslySetInnerHTML={{ __html: section.content }}
              />
            </div>
          );
        } else {
          // Content is object format - support multiple shapes:
          // 1) legacy { headers: [], rows: [] }
          // 2) Slate-style { type: 'table', children: [ { type: 'table-row', children: [ { type: 'table-header'|'table-cell', children: [...] } ] } ] }
          const contentObj = typeof section.content === 'string' ? { headers: [], rows: [] } : section.content;

          const getTextFromChildren = (children) => {
            if (!children) return '';
            if (typeof children === 'string') return children;
            if (Array.isArray(children)) {
              return children.map(ch => getTextFromChildren(ch.children || ch.text || ch)).join('');
            }
            if (children.text) return children.text;
            if (children.children) return getTextFromChildren(children.children);
            return '';
          };

          // Slate-style table
          if (contentObj.type === 'table' && Array.isArray(contentObj.children)) {
            const rows = contentObj.children.map(row => row.children || []);
            const hasHeader = rows.length > 0 && rows[0].every(cell => cell.type === 'table-header');
            const headerCells = hasHeader ? rows[0] : [];
            const bodyRows = hasHeader ? rows.slice(1) : rows;

            return (
              <div className="overflow-x-auto my-8">
                <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
                  {hasHeader && (
                    <thead>
                      <tr>
                        {headerCells.map((cell, idx) => (
                          <th key={idx} className="border border-gray-300 dark:border-gray-600 px-4 py-2 bg-gray-100 dark:bg-gray-700 font-semibold text-left">
                            <div dangerouslySetInnerHTML={{ __html: getTextFromChildren(cell.children) }} />
                          </th>
                        ))}
                      </tr>
                    </thead>
                  )}
                  <tbody>
                    {bodyRows.map((r, ridx) => (
                      <tr key={ridx}>
                        {r.map((cell, cidx) => (
                          <td key={cidx} className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                            <div dangerouslySetInnerHTML={{ __html: getTextFromChildren(cell.children) }} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }

          // Legacy { headers: [], rows: [] }
          const tableContent = contentObj;
          return (
            <div className="overflow-x-auto my-8">
              <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
                {tableContent.headers && tableContent.headers.length > 0 && (
                  <thead>
                    <tr>
                      {tableContent.headers.map((header, idx) => (
                        <th
                          key={idx}
                          className="border border-gray-300 dark:border-gray-600 px-4 py-2 bg-gray-100 dark:bg-gray-700 font-semibold text-left"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                )}
                <tbody>
                  {tableContent.rows?.map((row, rowIdx) => (
                    <tr key={rowIdx}>
                      {row.map((cell, cellIdx) => (
                        <td
                          key={cellIdx}
                          className="border border-gray-300 dark:border-gray-600 px-4 py-2"
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }

      case 'list':
        // Handle both HTML string (from RichTextEditor) and object format (legacy)
        if (typeof section.content === 'string' && (section.content.includes('<ul') || section.content.includes('<ol'))) {
          // Content is HTML - render it directly
          return (
            <div 
              className="my-6 prose prose-lg max-w-none dark:prose-invert"
              dangerouslySetInnerHTML={{ __html: section.content }}
            />
          );
        } else {
          // Content is object format (legacy support)
          const listContent = typeof section.content === 'string'
            ? { type: 'unordered', items: [] }
            : section.content;
          const ListTag = listContent.type === 'ordered' ? 'ol' : 'ul';
          const listStyle = listContent.type === 'ordered' ? 'list-decimal' : 'list-disc';
          
          return (
            <ListTag className={`${listStyle} list-inside space-y-2 my-6 ml-4`}>
              {listContent.items?.map((item, idx) => (
                <li key={idx} className="text-default">
                  {item}
                </li>
              ))}
            </ListTag>
          );
        }

      default:
        return null;
    }
  };

  return <div>{renderSection()}</div>;
};

export default BlogSection;
